import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Lock, Loader2, Users, Check, X, Trash2, LogOut } from "lucide-react";
import { toast } from "sonner";
import { API } from "./config";

const TOKEN_KEY = "ne_admin_token";

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleString("nl-NL", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

export default function Overzicht() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setData(null);
  }, []);

  const loadOverview = useCallback(
    async (tk) => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/admin/overview`, {
          headers: { Authorization: `Bearer ${tk}` },
        });
        setData(data);
      } catch (e) {
        if (e.response?.status === 401) {
          toast.error("Sessie verlopen, log opnieuw in");
          logout();
        } else {
          toast.error("Kon gegevens niet laden");
        }
      } finally {
        setLoading(false);
      }
    },
    [logout],
  );

  useEffect(() => {
    if (token) loadOverview(token);
  }, [token, loadOverview]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setPassword("");
    } catch {
      toast.error("Onjuist wachtwoord");
    } finally {
      setLoading(false);
    }
  };

  const deleteEntry = async (id) => {
    try {
      await axios.delete(`${API}/admin/guestbook/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Bericht verwijderd");
      loadOverview(token);
    } catch {
      toast.error("Verwijderen mislukt");
    }
  };

  // --- Login gate ---
  if (!token || !data) {
    return (
      <div className="min-h-screen grid place-items-center bg-ink px-6">
        <form
          onSubmit={login}
          data-testid="admin-login-form"
          className="w-full max-w-sm rounded-sm border border-gold/40 bg-cream p-8 text-center"
        >
          <span className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-turq text-cream">
            <Lock className="h-5 w-5" strokeWidth={1.7} />
          </span>
          <h1 className="mt-5 font-display text-3xl text-ink">Privé overzicht</h1>
          <p className="mt-2 font-sans text-sm text-ink/55">
            Nurhan &amp; Emre — RSVP &amp; gastenboek
          </p>
          <input
            type="password"
            data-testid="admin-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Wachtwoord"
            className="mt-6 w-full rounded-sm border border-ink/15 bg-white px-4 py-3 font-sans text-center tracking-widest text-ink outline-none focus:border-turq"
          />
          <button
            type="submit"
            data-testid="admin-login-submit"
            disabled={loading}
            className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-turq px-6 py-3 font-sans text-xs uppercase tracking-[0.16em] text-cream transition-colors hover:bg-ink disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
            Inloggen
          </button>
          <a href="/" className="mt-5 block font-sans text-xs text-ink/40 hover:text-turq">
            ← Terug naar de site
          </a>
        </form>
      </div>
    );
  }

  const s = data.summary;
  const stats = [
    { label: "Reacties", value: s.responses },
    { label: "Komen", value: s.attending },
    { label: "Kunnen niet", value: s.declined },
    { label: "Totaal gasten", value: s.total_guests },
  ];

  // --- Dashboard ---
  return (
    <div className="min-h-screen bg-cream" data-testid="admin-dashboard">
      <header className="flex items-center justify-between border-b border-gold/30 px-6 py-5 md:px-10">
        <div>
          <h1 className="font-display text-2xl text-ink">Privé overzicht</h1>
          <p className="font-sans text-xs uppercase tracking-[0.2em] text-ink/45">
            Nurhan &amp; Emre
          </p>
        </div>
        <button
          onClick={logout}
          data-testid="admin-logout"
          className="inline-flex items-center gap-2 rounded-full border border-ink/20 px-4 py-2 font-sans text-xs uppercase tracking-[0.14em] text-ink transition-colors hover:bg-ink hover:text-cream"
        >
          <LogOut className="h-4 w-4" /> Uitloggen
        </button>
      </header>

      <main className="mx-auto max-w-5xl px-6 py-10 md:px-10">
        {/* stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {stats.map((st) => (
            <div key={st.label} className="rounded-sm border border-gold/30 bg-white p-5">
              <p className="font-display text-4xl text-turq">{st.value}</p>
              <p className="mt-1 font-sans text-xs uppercase tracking-[0.16em] text-ink/50">
                {st.label}
              </p>
            </div>
          ))}
        </div>

        {/* RSVP list */}
        <div className="mt-10">
          <h2 className="flex items-center gap-2 font-display text-2xl text-ink">
            <Users className="h-5 w-5 text-turq" /> Aanmeldingen ({s.responses})
          </h2>
          <div className="mt-4 overflow-hidden rounded-sm border border-ink/10 bg-white">
            <table className="w-full text-left text-sm">
              <thead className="bg-cream font-sans text-xs uppercase tracking-wider text-ink/50">
                <tr>
                  <th className="px-4 py-3">Naam</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Pers.</th>
                  <th className="px-4 py-3 hidden md:table-cell">Telefoon</th>
                  <th className="px-4 py-3 hidden md:table-cell">Bericht</th>
                  <th className="px-4 py-3 hidden md:table-cell">Datum</th>
                </tr>
              </thead>
              <tbody className="font-sans text-ink/80">
                {data.rsvps.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-6 text-center text-ink/40">
                      Nog geen aanmeldingen
                    </td>
                  </tr>
                ) : (
                  data.rsvps.map((r) => (
                    <tr key={r.id} className="border-t border-ink/10" data-testid="admin-rsvp-row">
                      <td className="px-4 py-3 font-medium">{r.name}</td>
                      <td className="px-4 py-3">
                        {r.attending === "yes" ? (
                          <span className="inline-flex items-center gap-1 text-turq">
                            <Check className="h-4 w-4" /> Komt
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-pinky">
                            <X className="h-4 w-4" /> Nee
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">{r.guests}</td>
                      <td className="px-4 py-3 hidden md:table-cell">{r.phone || "—"}</td>
                      <td className="px-4 py-3 hidden md:table-cell max-w-xs truncate">
                        {r.note || "—"}
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell text-ink/50">
                        {fmtDate(r.created_at)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Guestbook */}
        <div className="mt-10">
          <h2 className="font-display text-2xl text-ink">
            Gastenboek ({s.guestbook_count})
          </h2>
          <div className="mt-4 space-y-3">
            {data.guestbook.length === 0 ? (
              <p className="font-sans text-sm text-ink/40">Nog geen berichten</p>
            ) : (
              data.guestbook.map((e) => (
                <div
                  key={e.id}
                  className="flex items-start justify-between gap-4 rounded-sm border border-ink/10 bg-white p-4"
                >
                  <div>
                    <p className="font-display text-lg italic text-ink">“{e.message}”</p>
                    <p className="mt-1 font-sans text-xs text-ink/50">
                      {e.name} · {fmtDate(e.created_at)}
                    </p>
                  </div>
                  <button
                    onClick={() => deleteEntry(e.id)}
                    data-testid="admin-delete-entry"
                    className="shrink-0 rounded-full p-2 text-ink/40 transition-colors hover:bg-pinky/10 hover:text-pinky"
                    aria-label="Verwijderen"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
